// Shared PadiHAT raw_payload field taxonomy - extracted from Debug.js's
// Rich Telemetry / Telemetry Graphs panels so any other consumer (the
// Experiment page's AdHocTelemetryPanel) offers exactly the same fields,
// grouped and labeled the same way, instead of hand-picking a subset that
// silently drifts from what Debug.js actually shows.
//
// Panels/groups with no data on a given payload are meant to be hidden by
// the caller (v0.2 devices publish a subset of keys; v0.3 adds ext_t/ext_h,
// tilt, ax/ay/az, t_sens/t_mdm/t_net/t_mqtt, solar_state, etc).

import { formatTilt, tiltFromAxes } from "utils/tilt";

// Fields the browser computes from fields the device does report, because the firmware's own
// value is misleading. They are offered and labelled like reported fields; charts request their
// source fields instead (seriesRequestFields) and fill them in per row (withDerivedSeries).
// tilt_from_axes: the firmware's `tilt` is acos(|az|) with az clamped to 1 g, so small tilts read
// exactly 0 (see utils/tilt.js). The firmware's value stays available, labelled as its own.
export const DERIVED_FIELDS = {
  tilt_from_axes: {
    from: ["ax", "ay", "az"],
    derive: (values) => tiltFromAxes(values.ax, values.ay, values.az),
    format: formatTilt,
  },
};

/** The payload plus every derived field its values allow (a field whose sources are missing is left out). */
export const withDerivedFields = (payload) => {
  if (!payload) return payload;
  const out = { ...payload };
  Object.entries(DERIVED_FIELDS).forEach(([key, field]) => {
    const value = field.derive(payload);
    if (value !== null && value !== undefined) out[key] = value;
  });
  return out;
};

export const TELEMETRY_GROUPS = [
  {
    title: "Identity & Firmware",
    // fw / boot / interval / cfg_id moved to status/ (device shadow) —
    // see Config sync panel on Config page. sha8/rst stay per-reading.
    keys: ["mac", "sha8", "rst", "ts"],
  },
  {
    title: "Wake / Sleep",
    keys: ["wake", "last_wake", "next_wake", "up", "batt_band"],
  },
  {
    title: "Environment",
    keys: ["t", "h", "p", "ext_t", "ext_h", "rtc_t", "chip_t"],
  },
  {
    title: "Power",
    keys: ["bv", "bi", "bw", "sv", "si", "sw", "chg", "solar_state"],
  },
  {
    title: "Distance",
    keys: ["d1", "d2", "d1_cal", "d2_cal", "d1_est", "d1_sd", "d1_n", "d1_n_total", "d1_raw_us"],
  },
  {
    title: "Location",
    keys: ["lat", "lon", "loc_type", "gps_fresh", "gps_age", "gps_sats", "gps_hdop"],
  },
  {
    title: "Motion (v0.3)",
    keys: ["ax", "ay", "az", "amag", "tilt_from_axes", "tilt"],
  },
  {
    title: "Cellular",
    keys: ["rssi", "ber", "op", "tech", "cell", "tac"],
  },
  {
    title: "Phase Timing (ms)",
    keys: ["t_sens", "t_mdm", "t_net", "t_mqtt"],
  },
  {
    title: "System",
    keys: ["heap", "heap_min", "psram", "flash_pct"],
  },
];

export const KEY_LABELS = {
  mac: "MAC", fw: "Firmware", sha8: "SHA-8", boot: "Boot #", rst: "Reset reason",
  interval: "Interval (s)", cfg_id: "Config ID", ts: "Timestamp (epoch)",
  wake: "Wake reason", last_wake: "Last wake", next_wake: "Next wake (epoch)",
  up: "Uptime (s)", batt_band: "Battery band",
  t: "Temp (°C)", h: "Humidity (%)", p: "Pressure (hPa)",
  ext_t: "Ext temp (°C)", ext_h: "Ext humidity (%)",
  rtc_t: "RTC temp (°C)", chip_t: "Chip temp (°C)",
  bv: "Batt V", bi: "Batt I (A)", bw: "Batt W",
  sv: "Solar V", si: "Solar I (A)", sw: "Solar W",
  chg: "Charging", solar_state: "Solar state",
  d1: "Dist 1 (cm)", d2: "Dist 2 (cm)",
  d1_cal: "Dist 1 cal (cm)", d2_cal: "Dist 2 cal (cm)",
  d1_est: "Dist 1 est (cm)",
  d1_sd: "Dist 1 σ", d1_n: "Dist 1 n valid", d1_n_total: "Dist 1 n total",
  d1_raw_us: "Dist 1 raw (µs, n)",
  d1_raw: "Dist 1 raw (cm)",
  lat: "Latitude", lon: "Longitude", loc_type: "Loc type",
  gps_fresh: "GPS fresh", gps_age: "GPS age (s)",
  gps_sats: "GPS sats", gps_hdop: "GPS HDOP",
  ax: "Accel X (g)", ay: "Accel Y (g)", az: "Accel Z (g)",
  amag: "Accel |a| (g)", tilt_from_axes: "Tilt (deg)",
  tilt: "Tilt reported by firmware (deg)",
  rssi: "RSSI (dBm)", ber: "BER", op: "Operator",
  tech: "Tech", cell: "Cell ID", tac: "TAC",
  t_sens: "Sensors", t_mdm: "Modem", t_net: "Network", t_mqtt: "MQTT",
  heap: "Heap free (B)", heap_min: "Heap min (B)",
  psram: "PSRAM (B)", flash_pct: "Flash used %",
};

export const formatTelemetryValue = (key, value) => {
  if (value === null || value === undefined) return "—";
  if (DERIVED_FIELDS[key]?.format && typeof value === "number")
    return DERIVED_FIELDS[key].format(value);
  if (typeof value === "boolean") return value ? "yes" : "no";
  if (Array.isArray(value)) {
    const valid = value.filter((v) => typeof v === "number" && v > 0);
    if (valid.length === 0) return `[${value.length}, all timeout]`;
    const min = Math.min(...valid);
    const max = Math.max(...valid);
    return `n=${valid.length}/${value.length}, ${min}–${max} µs`;
  }
  if (typeof value === "number") {
    if (key === "ts" || key === "next_wake") {
      try { return new Date(value * 1000).toLocaleString(); } catch { return String(value); }
    }
    if (Number.isInteger(value)) return value.toLocaleString();
    return value.toFixed(Math.abs(value) >= 100 ? 1 : 3);
  }
  return String(value);
};

/**
 * Derive tickable numeric/boolean field names from a device's latest raw
 * payload - so v0.2 vs v0.3 devices (and any future field) only ever offer
 * what they actually report, rather than a hard-coded list. Fields derived
 * from reported ones (DERIVED_FIELDS) are offered when their sources are.
 */
export const deriveNumericFields = (latestPayload) => {
  if (!latestPayload) return [];
  const payload = withDerivedFields(latestPayload);
  return Object.keys(payload)
    .filter((k) => {
      const v = payload[k];
      return typeof v === "number" || typeof v === "boolean";
    })
    .sort();
};

/** The telemetry-series fields to request for `fields`: derived fields are replaced by their sources. */
export const seriesRequestFields = (fields) => [
  ...new Set(fields.flatMap((f) => DERIVED_FIELDS[f]?.from || [f])),
];

/** Series rows with the derived fields among `fields` filled in (null where a source is missing). */
export const withDerivedSeries = (rows, fields) => {
  const derived = fields.filter((f) => DERIVED_FIELDS[f]);
  if (!derived.length) return rows;
  return rows.map((row) => {
    const out = { ...row };
    derived.forEach((key) => {
      out[key] = DERIVED_FIELDS[key].derive(row) ?? null;
    });
    return out;
  });
};
